import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Folder, SquaresFour, ListBullets, Info } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { nasApi } from './nasApi';
import NasFolderTree from './NasFolderTree';
import NasBreadcrumbs from './NasBreadcrumbs';
import NasFileCard from './NasFileCard';
import NasFileList from './NasFileList';
import NasFilePreview from './NasFilePreview';
import { NAS_I18N } from './nasI18n';

// Browse tab. Read-only source browsing. No DMS folder coupling.
export default function NasArchiveBrowse({ meta, roots }) {
  const [rootId, setRootId] = useState(roots[0]?.id || '');
  const [trail, setTrail] = useState([]); // [{node_id, name}]
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('grid');
  const [preview, setPreview] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const currentNodeId = trail.length ? trail[trail.length - 1].node_id : null;
  const root = roots.find((r) => r.id === rootId);
  const canDownload = !!meta?.flags?.nas_download;

  const load = useCallback(async () => {
    if (!rootId) return;
    setLoading(true); setDisabled(false);
    try {
      const d = await nasApi.tree(rootId, currentNodeId);
      setData(d);
      // Root redirection — a single approved root is entered automatically.
      if (d?.auto_enter?.node_id && !currentNodeId) {
        setTrail([{ node_id: d.auto_enter.node_id, name: d.auto_enter.name }]);
      }
    } catch (e) {
      if (e?.response?.status === 404) { setDisabled(true); }
      else toast.error(e?.response?.data?.detail || 'フォルダーを読み込めません');
      setData(null);
    } finally { setLoading(false); }
  }, [rootId, currentNodeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setTrail([]); }, [rootId]);
  // Sync the selected root once roots arrive (props are fetched async).
  useEffect(() => {
    if (!rootId && roots.length) setRootId(roots[0].id);
  }, [roots, rootId]);

  if (!roots.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-6 text-center" data-testid="nas-browse-noroots">
        <Info size={22} className="mx-auto text-amber-600 mb-2" />
        <p className="text-sm font-medium text-amber-900">{NAS_I18N.not_configured}</p>
        <p className="text-sm text-amber-800 mt-1">{NAS_I18N.not_configured_hint}</p>
      </div>
    );
  }
  if (disabled) {
    return <DisabledNotice />;
  }
  if (data?.scope_empty) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-6 text-center" data-testid="nas-scope-empty">
        <Info size={22} className="mx-auto text-amber-600 mb-2" />
        <p className="text-sm font-medium text-amber-900">{NAS_I18N.scope_empty_title}</p>
        <p className="text-sm text-amber-800 mt-1">{data.scope_message || NAS_I18N.scope_empty_hint}</p>
      </div>
    );
  }

  const openFolder = (node) => setTrail((t) => [...t, { node_id: node.node_id, name: node.name }]);
  const jumpTree = (node) => setTrail(node ? [{ node_id: node.node_id, name: node.name }] : []);
  const gotoCrumb = (i) => setTrail((t) => t.slice(0, i + 1));

  return (
    <div className="flex flex-col lg:flex-row gap-5" data-testid="nas-browse">
      <NasFolderTree rootId={rootId} roots={roots} selectedId={currentNodeId} onSelect={jumpTree} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          {roots.length > 1 && (
            <Select value={rootId} onValueChange={setRootId}>
              <SelectTrigger className="h-9 w-56 text-sm" aria-label="ソースを選ぶ" data-testid="nas-root-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roots.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.logical_name}{r.is_test ? '（テスト）' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <NasBreadcrumbs rootName={root?.logical_name} trail={trail}
            onRoot={() => setTrail([])} onCrumb={gotoCrumb} />
          {root?.is_test && (
            <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
              data-testid="nas-test-badge">{NAS_I18N.test_source}</span>
          )}
          <div className="ml-auto flex rounded-md border border-zinc-200 overflow-hidden" data-testid="nas-mode-switch">
            {[['grid', SquaresFour, 'サムネイル'], ['list', ListBullets, 'リスト']].map(([m, Icon, label]) => (
              <button key={m} type="button" onClick={() => setMode(m)} title={label} aria-label={label}
                aria-pressed={mode === m} data-testid={`nas-mode-${m}`}
                className={`h-9 w-9 flex items-center justify-center ${mode === m ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                <Icon size={15} />
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-zinc-400 mb-3 inline-flex items-center gap-1">
          <Info size={12} /> {NAS_I18N.read_only_note}
        </p>
        <p className="text-xs text-zinc-500 mb-3" data-testid="nas-scope-note">
          {NAS_I18N.scope_note}
        </p>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-400">読み込み中…</p>
        ) : !data ? null : (
          <>
            {(data.directories || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="nas-subfolders">
                {data.directories.map((d) => (
                  <button key={d.node_id} type="button" onClick={() => openFolder(d)}
                    data-testid={`nas-folder-${d.node_id}`}
                    className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-left hover:border-teal-300 hover:shadow-sm transition-[box-shadow,border-color] duration-200">
                    <Folder size={17} weight="duotone" className="text-zinc-400 shrink-0" />
                    <span className="text-sm text-zinc-800 truncate">{d.name}</span>
                  </button>
                ))}
              </div>
            )}
            {(data.files || []).length === 0 && (data.directories || []).length === 0 ? (
              <p className="py-16 text-center text-sm text-zinc-400" data-testid="nas-empty">このフォルダーは空です</p>
            ) : mode === 'list' ? (
              <NasFileList files={data.files || []} onOpen={(f) => setPreview(f.node_id)} />
            ) : (
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(200px,100%),1fr))]" data-testid="nas-file-grid">
                {(data.files || []).map((f) => (
                  <NasFileCard key={f.node_id} file={f} onOpen={(x) => setPreview(x.node_id)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {preview && (meta?.flags?.nas_preview
        ? <NasFilePreview nodeId={preview} canDownload={canDownload} onClose={() => setPreview(null)} />
        : (toast.error('プレビューは無効です'), setPreview(null), null))}
    </div>
  );
}

function DisabledNotice() {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center" data-testid="nas-browse-disabled">
      <Info size={22} className="mx-auto text-zinc-400 mb-2" />
      <p className="text-sm font-medium text-zinc-700">{NAS_I18N.disabled_title}</p>
      <p className="text-sm text-zinc-500 mt-1">{NAS_I18N.disabled_hint}</p>
    </div>
  );
}
